import { useEffect } from 'react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';
import { Building2, Users, Heart, Briefcase, CheckCircle, Sprout } from 'lucide-react';

interface ClientsPageProps {
  onNavigate: (path: string) => void;
  onSignIn: () => void;
  onSignUp: () => void;
}

export function ClientsPage({ onNavigate, onSignIn, onSignUp }: ClientsPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Trezoone est-il adapté aux associations loi 1901 ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, Trezoone est parfaitement adapté aux associations loi 1901. Le suivi de trésorerie et les rapports financiers mensuels sont utiles pour le bureau et le conseil d'administration."
          }
        },
        {
          "@type": "Question",
          "name": "Combien de temps pour mettre en place Trezoone ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "La mise en place de Trezoone prend environ 15 minutes : connexion bancaire, catégorisation initiale des transactions, et première génération de rapport."
          }
        },
        {
          "@type": "Question",
          "name": "À quelle fréquence consulter ses indicateurs financiers ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Il est recommandé de consulter vos indicateurs financiers au minimum une fois par mois lors de la génération du rapport mensuel."
          }
        },
        {
          "@type": "Question",
          "name": "Qu'est-ce qu'un score de pilotage financier ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Le score de pilotage financier évalue la qualité de votre suivi financier. Il prend en compte la régularité de votre suivi, la santé de votre trésorerie, et la stabilité de vos indicateurs."
          }
        },
        {
          "@type": "Question",
          "name": "Trezoone gère-t-il les cycles saisonniers agricoles ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, Trezoone permet de créer des prévisions de trésorerie annuelles qui prennent en compte vos cycles de production, vos rentrées saisonnières (récoltes, aides PAC) et vos charges périodiques (intrants, main-d'œuvre)."
          }
        }
      ]
    };

  return (
    <>
      <SeoHead
        title="Trezoone – Pilotage financier pour TPE, PME, exploitations agricoles et associations"
        description="Trezoone s'adresse aux TPE, PME, exploitations agricoles et associations qui souhaitent suivre leur trésorerie, comprendre leurs chiffres et piloter leur activité sans complexité."
        canonical="/clients"
        keywords="pilotage financier TPE, gestion trésorerie PME, gestion financière association, comptabilité exploitation agricole"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-gray-50">
        <MarketingHeader onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />

      <main className="max-w-5xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          Trezoone pour chaque type de dirigeant
        </h1>

        <p className="text-xl text-gray-600 mb-12">
          TPE, PME, exploitations agricoles, associations, structures sans DAF : Trezoone s'adapte à votre organisation.
        </p>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            TPE et dirigeants de petites structures
          </h2>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-start space-x-4">
              <Building2 className="h-8 w-8 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Suivre sa trésorerie simplement
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  En tant que dirigeant de TPE, vous n'avez pas le temps de passer des heures sur vos chiffres.
                  Trezoone vous donne une vision claire de votre trésorerie en quelques minutes par mois.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Comprendre ses chiffres sans jargon
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Les rapports financiers mensuels Trezoone sont rédigés en langage clair. Vous comprenez vos indicateurs
              sans avoir besoin d'une formation financière.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            PME sans DAF
          </h2>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-start space-x-4">
              <Briefcase className="h-8 w-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Pilotage financier sans équipe finance
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Votre PME n'a pas de directeur financier ? Trezoone vous donne les outils pour piloter votre trésorerie
                  et suivre vos indicateurs comme si vous aviez une équipe finance.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Vision mensuelle claire et synthétique
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Chaque mois, vous recevez un rapport complet qui synthétise votre situation financière.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Associations
          </h2>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-start space-x-4">
              <Heart className="h-8 w-8 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Suivi de trésorerie associatif
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Les associations ont besoin de transparence financière. Trezoone génère des rapports financiers mensuels
                  clairs et compréhensibles par des bénévoles non financiers.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Rapport financier clair pour bureau et conseil d'administration
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Les rapports financiers mensuels sont exportables en PDF et prêts à être présentés en réunion.
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg shadow-sm p-8 border-2 border-red-200 mt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <Heart className="h-8 w-8 text-red-700 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Solution spécialisée pour associations
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Découvrez comment Trezoone s'adapte aux besoins spécifiques des associations :
                    suivi des subventions, transparence financière, rapports pour le CA et gestion
                    des multiples comptes bancaires.
                  </p>
                  <button
                    onClick={() => onNavigate('/gestion-tresorerie-association')}
                    className="inline-flex items-center gap-2 bg-red-700 text-white px-6 py-3 rounded-lg hover:bg-red-800 transition-colors font-semibold"
                  >
                    Découvrir la solution pour associations
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Exploitations agricoles et viticoles
          </h2>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-start space-x-4">
              <Sprout className="h-8 w-8 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Trésorerie agricole et cycles saisonniers
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Le secteur agricole présente des défis financiers uniques : cycles saisonniers, aléas climatiques,
                  investissements lourds en matériel. Trezoone vous aide à piloter votre trésorerie sur l'année complète
                  en tenant compte de vos cycles de production, des aides PAC et des avances remboursables.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Gestion des subventions et prévisions annuelles
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Anticipez vos rentrées d'argent (ventes de récoltes, aides PAC, subventions) et vos dépenses
              (charges de mécanisation, intrants, main-d'œuvre saisonnière). Les prévisions de trésorerie
              s'adaptent aux spécificités agricoles pour éviter les découverts en intersaison.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Rapports pour banques et organismes agricoles
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Les rapports financiers mensuels facilitent vos échanges avec votre banquier, votre expert-comptable
              et les organismes de financement agricole. Présentez des chiffres clairs pour vos demandes de prêts
              ou de restructuration de dettes.
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-sm p-8 border-2 border-green-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <Sprout className="h-8 w-8 text-green-700 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      TrezoOne Agriculture
                    </h3>
                    <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      En développement
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Une solution spécialisée pour piloter vos campagnes agricoles du semis à l'encaissement.
                    Anticipez votre trésorerie, gérez la volatilité des prix, suivez vos subventions PAC et
                    simulez l'impact des aléas climatiques sur votre exploitation.
                  </p>
                  <button
                    onClick={() => onNavigate('/gestion-tresorerie-agriculteur')}
                    className="inline-flex items-center gap-2 bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-800 transition-colors font-semibold"
                  >
                    Découvrir TrezoOne Agriculture
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Dirigeants multi-structures
          </h2>

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-start space-x-4">
              <Users className="h-8 w-8 text-purple-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Pilotage financier multi-activités
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Vous dirigez plusieurs entreprises ? Trezoone vous permet de suivre la trésorerie et les indicateurs
                  de chaque structure de manière indépendante.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Comparaison et lecture transversale
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Les rapports de chaque structure utilisent les mêmes indicateurs. Vous pouvez facilement comparer les performances.
            </p>
          </div>
        </section>

        <section className="mb-16 bg-blue-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Exemples d'usages concrets
          </h2>

          <div className="space-y-4">
            {[
              { title: 'Cabinet de conseil', desc: '5 salariés, suivi mensuel pour anticiper les charges sociales et investissements' },
              { title: 'Association sportive', desc: '200 adhérents, rapports mensuels pour bureau et assemblée générale' },
              { title: 'PME de distribution', desc: '15 salariés, pilotage financier sans DAF, identification des principales charges' },
              { title: 'Exploitation viticole', desc: '80 hectares, gestion des cycles vendanges, aides PAC, investissements chai et matériel' },
              { title: 'GAEC grandes cultures', desc: '3 associés, prévisions annuelles adaptées aux cycles de semis et moissons, suivi des avances' },
              { title: 'Auto-entrepreneur', desc: 'Suivi simple de trésorerie, calcul automatique du résultat mensuel' },
              { title: 'Dirigeant de 3 SAS', desc: 'Suivi indépendant de chaque structure, vision transversale du groupe' },
              { title: 'Association culturelle', desc: 'Gestion des subventions, suivi des recettes, transparence pour les financeurs' }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">{item.title}</p>
                  <p className="text-gray-600 mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Questions fréquentes
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Trezoone est-il adapté aux associations loi 1901 ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Oui, Trezoone est parfaitement adapté aux associations loi 1901. Les indicateurs sont présentés
                de manière claire pour des bénévoles non financiers.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Combien de temps pour mettre en place Trezoone ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                La mise en place prend environ 15 minutes. Ensuite, le système fonctionne de manière automatisée.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                À quelle fréquence consulter ses indicateurs financiers ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Il est recommandé de consulter vos indicateurs au minimum une fois par mois lors du rapport mensuel.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Qu'est-ce qu'un score de pilotage financier ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Le score de pilotage évalue la qualité de votre suivi financier : régularité du suivi,
                santé de la trésorerie, et stabilité des indicateurs.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Trezoone gère-t-il les cycles saisonniers agricoles ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Oui, Trezoone permet de créer des prévisions de trésorerie annuelles qui prennent en compte
                vos cycles de production, vos rentrées saisonnières (récoltes, aides PAC) et vos charges
                périodiques (intrants, main-d'œuvre). Vous pouvez simuler différents scénarios en fonction
                des aléas climatiques ou des variations de prix.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            En bref : glossaire
          </h2>

          <dl className="space-y-4">
            <div>
              <dt className="font-semibold text-gray-900">TPE</dt>
              <dd className="text-gray-600 mt-1">Très Petite Entreprise, généralement moins de 10 salariés.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">PME</dt>
              <dd className="text-gray-600 mt-1">Petite et Moyenne Entreprise, entre 10 et 250 salariés.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">DAF</dt>
              <dd className="text-gray-600 mt-1">Directeur Administratif et Financier.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Trésorerie</dt>
              <dd className="text-gray-600 mt-1">Argent disponible sur vos comptes bancaires.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Pilotage financier</dt>
              <dd className="text-gray-600 mt-1">Action de suivre et analyser régulièrement les indicateurs financiers.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">GAEC, EARL, SCEA</dt>
              <dd className="text-gray-600 mt-1">Formes juridiques agricoles : Groupement Agricole d'Exploitation en Commun, Exploitation Agricole à Responsabilité Limitée, Société Civile d'Exploitation Agricole.</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">Aides PAC</dt>
              <dd className="text-gray-600 mt-1">Politique Agricole Commune : subventions européennes versées aux agriculteurs selon leurs surfaces et pratiques.</dd>
            </div>
          </dl>
        </section>

        <div className="bg-blue-600 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Trezoone s'adapte à votre organisation
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => onNavigate('/tarifs')}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
            >
              Voir les tarifs
            </button>
            <button
              onClick={onSignUp}
              className="bg-blue-700 text-white px-8 py-3 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
            >
              S'inscrire gratuitement
            </button>
          </div>
        </div>
      </main>

        <MarketingFooter />
      </div>
    </>
  );
}
